import { w3cwebsocket as W3CWebSocket } from 'websocket'
import { WSSERVER_URL } from '../config'
import handlers from './handlers'
import { authPacket } from './packets/auth.packet'
import Cookie from 'js-cookie'

type IWsData = any

export type IWsPacket<Data> = {
    type: WsSendType | WsRecvType
    data?: Data
    message?: string
}

export type IWsHandler<Data> = (data: IWsPacket<Data>) => void

export let wsClient: WsClient

export function initWsClient(sessionIdFromUser?: string) {
    if (wsClient) wsClient.close()
    wsClient = new WsClient(sessionIdFromUser)
}

class WsClient {
    private client: W3CWebSocket
    private handlers: Map<WsRecvType, IWsHandler<IWsData>>
    private sessionId: string
    private manuallyClosed = false

    constructor(sessionIdFromUser?: string) {
        const sessionId = sessionIdFromUser || Cookie.get('cc.sid')
        if (sessionId === undefined || sessionId.length === 0) throw new Error('Empty session id')
        this.sessionId = sessionId

        this.client = new W3CWebSocket(WSSERVER_URL, 'echo-protocol')
        this.handlers = new Map()
        this.listen()
    }

    public close() {
        this.manuallyClosed = true
        this.client.close()
    }

    private registerHandlers() {
        for (const handler of handlers) {
            handler()
        }
    }

    public submitHandler(type: WsRecvType, handler: IWsHandler<IWsData>) {
        if (this.handlers.get(type) !== undefined) throw new Error(`Duplicate packet handlers: ${type}`)
        this.handlers.set(type, handler)
    }

    private handlePacket(packet: IWsPacket<IWsData>) {
        if (packet.type === undefined || packet.data === undefined) {
            console.log('%c[WsClient] Invalid packet.', packet, 'color: orange', packet)
            return
        }
        const handler = this.handlers.get(packet.type as WsRecvType)
        if (handler === undefined) {
            console.log(`%c[WsClient] Unhandled packet: ${packet.type}.`, 'color: orange', packet)
            return
        }
        handler(packet)
    }

    private listen() {
        this.client.onerror = () => {
            console.log('%c[WsClient] Connection Error', 'color: red')
        }

        this.client.onopen = () => {
            this.manuallyClosed = false
            console.log('%c[WsClient] Client Connected', 'color: green')
            this.registerHandlers()
            this.sendPacket(authPacket(this.sessionId))
            // appRef.setState({ serverStatus: true })
        }

        this.client.onclose = e => {
            console.log('%c[WsClient] Client Closed', 'color: red')
            if (this.manuallyClosed) return
            // Reconnect on any unexpected close (clean or unclean).
            setTimeout(() => initWsClient(this.sessionId), 1000)
        }

        this.client.onmessage = e => {
            if (typeof e.data === 'string') {
                // console.log(`%c[WsClient] Received: '${e.data}'`, 'color: #1b89cd')
                const p: IWsPacket<IWsData> = JSON.parse(e.data)
                if (p instanceof Array) {
                    for (const packet of p) {
                        this.handlePacket(packet)
                    }
                } else {
                    this.handlePacket(p)
                }
            } else {
                console.log('%c[WsClient] Unhandled MessageType.', 'color: red')
            }
        }
    }

    public sendPacket(data: IWsPacket<IWsData> | IWsPacket<IWsData>[]) {
        console.log('[WsClient] sendPacket', data)
        this.client.send(JSON.stringify(data))
    }

    isConnected() {
        return this.client.OPEN !== 0
    }
}

export enum WsSendType {
    Auth = 0,
    QuickSupportOpen = 1,
    QuickSupportClose = 2,
    QuickSupportData = 3,
}

export enum WsRecvType {
    AuthResult = 0,
    Error = 1,
    Message = 2,
    CustomerTraffic = 3,
    MachineOtaProgress = 4,
    MachineOtaWriteError = 5,
    ChannelCalibrationStatus = 6,
    QuickSupportStatus = 7,
    QuickSupportData = 8,
    VolumeMeasureStatus = 9,
    LiveLogNotification = 10,
    NotificationChanged = 11,
}
